import type { EnvSnapshot } from '@/api/schemas';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { EnvPill, PlatformIcon, ScopeChip, Tag, VisibilityBadge } from '@/components/badges';
import { RowsSkeleton } from './RowsSkeleton';
import { overridesText, secretCount, sourceText, type SnapshotEntry } from './envSnapshotModel';

/**
 * Immutable env snapshot of a build: key, scope (+ environment/platform), visibility, fingerprint, source and the
 * definitions it overrode. Never a value — the API only returns previews for plain vars and fingerprints for the rest,
 * and this tab shows fingerprints only so a secret can never leak through a preview column.
 */
export function BuildEnvSnapshot({ snapshot, isLoading, error, onRetry }: { snapshot: EnvSnapshot | undefined; isLoading: boolean; error: unknown; onRetry: () => void }) {
  if (isLoading && !snapshot) return <RowsSkeleton rowClassName="h-8 w-full" />;
  if (error && !snapshot) return <ErrorState compact error={error} onRetry={onRetry} />;
  if (!snapshot) return <EmptyState compact icon="tune" title="No snapshot" description="The env snapshot is taken when the build is queued." />;
  return (
    <div data-testid="build-env-snapshot">
      <SnapshotHeader snapshot={snapshot} />
      {/* A stacked list, not a table: the tab lives in the left column of the detail page (412–752 px), too narrow for five
          columns. From 36rem of list width the metadata sits on the right of the key. */}
      <ul aria-label="Env snapshot" className="@container divide-y">
        {snapshot.entries.map((e) => (
          <EntryRow key={`${e.key}-${e.scope}`} entry={e} />
        ))}
      </ul>
      {snapshot.overrides.length > 0 ? (
        <p className="border-t px-4 py-2 font-mono text-xs text-subtle sm:px-5">trigger overrides: {overridesText(snapshot.overrides)}</p>
      ) : null}
    </div>
  );
}

function SnapshotHeader({ snapshot }: { snapshot: EnvSnapshot }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-3 font-mono text-xs text-muted-foreground sm:px-5">
      <span className="flex items-center gap-1.5">
        <Icon name="fingerprint" size={16} className="text-subtle" />
        content hash <span className="break-all text-foreground">{snapshot.contentHash}</span>
      </span>
      <EnvPill env={snapshot.environment} />
      <PlatformIcon platform={snapshot.platform} withLabel />
      <span className="tabular ml-auto text-subtle">
        {snapshot.entries.length} vars · {secretCount(snapshot.entries)} secret
      </span>
    </div>
  );
}

function EntryRow({ entry: e }: { entry: SnapshotEntry }) {
  return (
    <li data-testid={`build-env-${e.key}`} className="grid gap-x-4 gap-y-1 px-4 py-2.5 sm:px-5 @xl:grid-cols-[minmax(0,1fr)_auto] @xl:items-center">
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate font-mono text-[13px] text-foreground" title={e.key}>
          {e.key}
        </span>
        <VisibilityBadge visibility={e.visibility} />
      </div>
      <EntryMeta entry={e} />
      {e.overriddenBy.length > 0 ? <EntryOverrides overriddenBy={e.overriddenBy} /> : null}
    </li>
  );
}

function EntryMeta({ entry: e }: { entry: SnapshotEntry }) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 font-mono text-xs text-subtle @xl:justify-end">
      <span className="flex items-center gap-1">
        <ScopeChip scope={e.scope} environment={e.environment} platform={e.platform} />
        {e.version ? <Tag>v{e.version}</Tag> : null}
      </span>
      {e.valueFingerprint ? (
        <span className="flex items-center gap-1 text-muted-foreground" title="Value fingerprint (HMAC); the value itself never leaves the API">
          <Icon name="fingerprint" size={14} className="shrink-0 text-subtle" />
          {e.valueFingerprint}
        </span>
      ) : null}
      <span>{sourceText(e)}</span>
    </div>
  );
}

function EntryOverrides({ overriddenBy }: { overriddenBy: SnapshotEntry['overriddenBy'] }) {
  return (
    <span className="flex flex-wrap items-center gap-1 text-[11px] text-subtle @xl:col-span-2">
      overrides
      {overriddenBy.map((o, i) => (
        <ScopeChip key={i} scope={o.scope} environment={o.environment} platform={o.platform} muted />
      ))}
    </span>
  );
}
